import { intervalToDuration, isToday, isTomorrow, isYesterday } from 'date-fns';
import { TFunction } from 'i18next';
import { formatDate } from 'shared/dates';
import { AlertType } from 'features/alerts/alerts.types';
import { MapLayer, MapLayers } from 'features/mapping/mapping.constants';

export type AlertStatus = 'active' | 'future' | 'expired';

export const getAlertStatus = (
  isActive: boolean,
  startDate: Date | null,
): AlertStatus => {
  if (!isActive) return 'expired';
  if (startDate && startDate > new Date()) return 'future';
  return 'active';
};

export const formatDay = (date: Date, t: TFunction): string => {
  if (isToday(date)) return t('alert.today');
  if (isYesterday(date)) return t('alert.yesterday');
  if (isTomorrow(date)) return t('alert.tomorrow');
  return formatDate(date, 'EEE');
};

export const timeLeft = (
  t: TFunction,
  target: Date,
  now = new Date(),
): string | null => {
  if (target <= now) return null;
  const {
    days = 0,
    hours = 0,
    minutes = 0,
  } = intervalToDuration({
    start: now,
    end: target,
  });
  const parts: string[] = [];
  if (days > 0) parts.push(t('alert.daysAbbrev', { days }));
  if (hours > 0) parts.push(t('alert.hoursAbbrev', { hours }));
  if (minutes > 0) parts.push(t('alert.minutesAbbrev', { minutes }));
  return parts.join(' ') || null;
};

const ALERT_TYPE_TO_TITLE_KEY: Record<AlertType, string> = {
  wea: 'home.weaAlertTitle',
  nws_fire: 'home.nwsAlertTitle',
  nws_flood: 'home.nwsAlertTitle',
  nws_evacuation: 'home.nwsAlertTitle',
};

export const getAlertPageTitle = (
  type: AlertType,
  title: string,
  t: TFunction,
): string => {
  const titleKey = ALERT_TYPE_TO_TITLE_KEY[type] || 'home.genericAlertTitle';
  return t(titleKey, { name: title });
};

export const getDateLabels = (
  status: AlertStatus,
  t: TFunction,
): { startLabel: string; endLabel: string } => {
  if (status === 'expired') {
    return { startLabel: t('alert.started'), endLabel: t('alert.ended') };
  }
  if (status === 'future') {
    return { startLabel: t('alert.starts'), endLabel: t('alert.ends') };
  }
  return { startLabel: t('alert.start'), endLabel: t('alert.end') };
};

export const getLayerNameForAlertType = (
  type?: AlertType,
): MapLayer | undefined => {
  if (type === 'nws_fire') {
    return MapLayers.RED_FLAG_WARNINGS;
  }
  if (type === 'nws_flood') {
    return MapLayers.FLOOD_WEATHER;
  }
  return undefined;
};
